import { useCallback, useRef } from 'react';

const SIMULTANEOUS_THRESHOLD = 150;

function isExcluded(target, excludeSelectors) {
  if (!target || !(target instanceof Element)) return false;
  return excludeSelectors.some(
    (selector) => target.matches(selector) || target.closest(selector)
  );
}

/** Three fingers down together, same trigger as the AI gift shop. */
export function useTripleTouch({ onTripleTouch, excludeSelectors = [] } = {}) {
  const isThreeFingerTouch = useRef(false);
  const activeTouches = useRef(new Map());

  const handleTouchStart = useCallback(
    (event) => {
      if (isExcluded(event.target, excludeSelectors)) return;

      const now = Date.now();
      Array.from(event.touches).forEach((touch) => {
        if (!activeTouches.current.has(touch.identifier)) {
          activeTouches.current.set(touch.identifier, now);
        }
      });

      if (event.touches.length === 3 && !isThreeFingerTouch.current) {
        const touchTimes = Array.from(activeTouches.current.values());
        if (touchTimes.length === 3) {
          const timeDiff = Math.max(...touchTimes) - Math.min(...touchTimes);
          if (timeDiff <= SIMULTANEOUS_THRESHOLD) {
            isThreeFingerTouch.current = true;
            onTripleTouch?.(event);
          }
        }
      } else if (event.touches.length !== 3) {
        isThreeFingerTouch.current = false;
      }
    },
    [excludeSelectors, onTripleTouch]
  );

  const handleTouchEnd = useCallback((event) => {
    const activeIds = new Set(Array.from(event.touches).map((touch) => touch.identifier));
    activeTouches.current.forEach((_, touchId) => {
      if (!activeIds.has(touchId)) activeTouches.current.delete(touchId);
    });
    if (isThreeFingerTouch.current && event.touches.length !== 3) {
      isThreeFingerTouch.current = false;
    }
    if (event.touches.length === 0) activeTouches.current.clear();
  }, []);

  const handleTouchMove = useCallback((event) => {
    if (isThreeFingerTouch.current && event.touches.length !== 3) {
      isThreeFingerTouch.current = false;
    }
  }, []);

  const reset = useCallback(() => {
    isThreeFingerTouch.current = false;
    activeTouches.current.clear();
  }, []);

  return { handleTouchStart, handleTouchEnd, handleTouchMove, reset };
}
